import React, { useMemo, useState } from "react";
import styled, { css } from "styled-components";
import Section from "./Section";
import Container from "./Container";
import ArrowDownIcon from "./Icons/ArrowDown";
import { AccessoryList } from "./AccessoryList";
import { ProductStars } from "./ProductStars";
import { ProductReviews } from "./ProductReviews";
import { Content, HtmlContent } from "./Content";
import { ProductDownload } from "./ProductDownload";
import { ProductApplications } from "./ProductApplications";
import { ProductSpecification } from "./ProductSpecification";
import randomstring from "randomstring"

const AccordionStyles = styled.div`
  ${({ ...props }) => css`
    > div {
      border-top: solid 1px ${props.theme.colors.lightGrey};

      &:last-child {
        border-bottom: solid 1px ${props.theme.colors.lightGrey};
      }
      .section--grey & {
        border-color: ${props.theme.colors.darkPrimary}
      }
    }
  `}
`;

const ButtonStyles = styled.button`
  ${({ ...props }) => css`
    background: none;
    color: ${props.theme.colors.darkGrey};
    text-transform: unset;
    font-size: inherit;
    letter-spacing: inherit;
    padding: 8px 16px;
    width: 100%;
    text-align: left;
    display: flex;
    justify-content: space-between;
    align-items: center;
    line-height: inherit;
    transition: all ease-in-out 0.3s;

    &:hover {
      background: ${props.theme.colors.lightGrey};
      color: ${props.theme.colors.darkPrimary};
    }

    &:focus {
      outline: none;
    }

    .accordion-icon svg {
      flex-shrink: 0;
      flex-grow: 0;
      margin-left: 24px;
      transition: all ease 0.3s;
      transform: rotate(${(props.$opened ? "180deg" : "0deg")});
    }

    border-radius: 0px;

    @media (min-width: ${props.theme.breakpoints.md}) {
      padding: 16px;
    }
  `}
`;

const ContentStyles = styled.div`
  ${({ ...props }) => css`
    display: ${(props.$opened ? "block" : "none")};
    padding: 24px 16px;
  `}
`;

const ReviewTitleStyles = styled.div`
  ${({ ...props }) => css`
    display: flex;
    align-items: center;
    > div {
      margin: 0;
      display: inline-flex;
      margin-left: 16px;
      flex-wrap: wrap;
    }
    svg {
      height: 16px;
      width: 16px;
    }
  `}
`;

const ProductDownloadStyles = styled.div`
  ${({ ...props }) => css`
    display: flex;
    flex-wrap: wrap;
    column-gap: 2rem;
    row-gap: 1rem;
    a {
      margin-top: 0;
    }
  `}
`;

const SpecAndAppStyles = styled.div`
${({ ...props }) => css`
  @media (min-width: ${props.theme.breakpoints.lg}) {
    &:has(div + div) {
      display: ${props.$fullWidth ? 'flex' : 'block'};
      column-gap: 2rem;
      div {
        width: 100%;
      }
      div + div {
        ${props.$fullWidth && 'margin-top: 0;'}
      }
    }
  }
`}
`;

const AccordionItem = ({ title, children }) => {
  const [isOpen, setOpen] = useState(false);

  const rand = useMemo(() => randomstring.generate(5), []);;

  const buttonID = `drawer-button-${rand}`;
  const panelID = `drawer-panel-${rand}`;

  return (
    <div>
      <h3>
        <ButtonStyles
          type="button"
          id={buttonID}
          aria-controls={panelID}
          aria-expanded={isOpen}
          onClick={() => setOpen(!isOpen)}
          $opened={isOpen}
        >
          {typeof title === 'string' ? <span><HtmlContent htmlString={title} /></span> : title}
          <span className="accordion-icon">
            <ArrowDownIcon  />
          </span>
        </ButtonStyles>
      </h3>
      <ContentStyles
        id={panelID}
        tabIndex={!isOpen ? -1 : 'auto'}
        aria-labelledby={buttonID}
        aria-hidden={!isOpen}
        $opened={isOpen}
      >
        {children}
      </ContentStyles>
    </div>
  );
};

export const Accordion = ({
  accessories,
  freeText,
  page,
  showReviews = true,
  showDownloads = false,
  showSpecifications = false,
  showApplications = false,
  fullWidth = false,
  background = 'white'
}) => {
  const hasAccessories = Array.isArray(accessories) && accessories?.length !== 0
  const hasFreeText = Array.isArray(freeText) && freeText?.length !== 0

  return (
    <Section
      grey={background === 'grey'}
    >
      <Container
        slim={!fullWidth}
      >
        <AccordionStyles>
          {showSpecifications && (
            <AccordionItem
              title={'Specifications'}
            >
              <SpecAndAppStyles
                $fullWidth={fullWidth}
              >
                {page.productData.specification && (
                  <ProductSpecification
                    specification={page.productData.specification}
                    grey={background === 'grey'}
                  />
                )}
                {showApplications && page.applications.nodes.length > 0 && (
                  <ProductApplications
                    applications={page.applications.nodes}
                    grey={background === 'grey'}
                  />
                )}
              </SpecAndAppStyles>
            </AccordionItem>
          )}
          {showDownloads && (
            <AccordionItem
              title={'Downloads'}
            >
              <ProductDownloadStyles>
                {/* PDF Download */}
                {page.productData.specification?.brochureDownload?.pdf?.localFile?.publicURL && (
                  <ProductDownload
                    label="Download Spec Sheet"
                    url={page.productData.specification.brochureDownload.pdf.localFile.publicURL}
                  />
                )}
                {/* Install guide */}
                {page.productData.specification?.installGuide.pdf?.localFile?.publicURL && (
                  <ProductDownload
                    label="Download Install guide"
                    url={page.productData.specification.installGuide.pdf.localFile.publicURL}
                  />
                )}
              </ProductDownloadStyles>
            </AccordionItem>
          )}
          {hasAccessories &&
            <AccordionItem
              title={'Accessories'}
            >
              <AccessoryList
                accessories={accessories}
                category="#3C6F83"
              />
            </AccordionItem>
          }
          {showReviews &&
            <AccordionItem
              title={(
                <ReviewTitleStyles>
                  Reviews
                  <ProductStars
                    productId={page.id}
                    className="product-stars"
                  />
                </ReviewTitleStyles>
              )}
            >
              <ProductReviews
                productId={page.id}
                name={page.title}
                source={page.uri}
                isGreyBg={background === 'grey'}
                displayProductStars={false}
              />
            </AccordionItem>
          }
          {hasFreeText &&
            freeText.map((elem, index) => (
              <AccordionItem
                title={elem.title}
                key={elem.title + index}
              >
                <Content content={elem.content} />
              </AccordionItem>
            ))
          }
        </AccordionStyles>
      </Container>
    </Section>
  );
};
